<template>
  <v-dialog
    :model-value="modelValue"
    max-width="760"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card tk-dialog">
      <div class="dialog-header tk-dialog__header">
        <div class="tk-dialog__header-main">
          <div class="dialog-header__icon tk-dialog__icon-wrap">
            <v-icon size="22">mdi-clipboard-list-outline</v-icon>
          </div>
          <div class="tk-dialog__head-copy">
            <span class="dialog-header__title">
              {{ isEditMode ? 'Edit campaign' : 'Create campaign' }}
            </span>
            <span class="tk-dialog__subtitle">
              {{
                isEditMode
                  ? 'Update the basics. Changing products, distribution, or agent field re-selects leads (merge-only — existing leads stay). Filters, the call script, transfer routing and auto-dial live on this campaign\'s Settings tab.'
                  : 'Queue leads from selected products for calling. The call script, transfer routing and everything else lives on the campaign\'s Settings tab right after.'
              }}
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="dialog-close-btn"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body tk-dialog__body">
        <v-form ref="formRef" v-model="isValid">
          <section class="tk-dialog__section">
            <p class="tk-dialog__section-title">Campaign details</p>

            <div class="tk-dialog__field">
              <label class="tk-dialog__label">Campaign name<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
              <v-text-field
                v-model="form.task_name"
                placeholder="e.g. July outbound follow-ups"
                variant="outlined"
                density="default"
                hide-details="auto"
                class="tk-dialog__input"
                :rules="[required('Campaign name')]"
                :disabled="saving"
              />
            </div>

            <div class="tk-dialog__field">
              <label class="tk-dialog__label">Description
                <span class="tk-dialog__label-hint">optional</span>
              </label>
              <v-textarea
                v-model="form.description"
                placeholder="What is this campaign about?"
                variant="outlined"
                density="default"
                rows="2"
                auto-grow
                hide-details="auto"
                class="tk-dialog__input tk-dialog__input--area"
                :disabled="saving"
              />
            </div>

            <div class="tk-dialog__field">
              <label class="tk-dialog__label">Product<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
              <v-select
                v-model="selectedContext"
                :items="contextOptions"
                item-title="title"
                item-value="value"
                placeholder="Leads from this product are queued"
                variant="outlined"
                density="default"
                hide-details="auto"
                class="tk-dialog__input"
                :loading="contextsLoading"
                :rules="[required('Product')]"
                :disabled="saving"
              />
            </div>

            <div class="tk-dialog__field">
              <label class="tk-dialog__label">Campaign type</label>
              <v-btn-toggle
                v-model="form.task_type"
                mandatory
                density="comfortable"
                class="tk-dialog__toggle"
                :disabled="saving || isEditMode"
              >
                <v-btn value="STANDARD" class="text-none">One-time campaign</v-btn>
                <v-btn value="RECURRING_REMINDER" class="text-none">Recurring reminder</v-btn>
              </v-btn-toggle>
              <p class="tk-dialog__hint">
                {{
                  isRecurringReminder
                    ? 'Every lead is reminded on its own repeating cycle (e.g. every 90 days from its own activation date) until you deactivate this campaign — there is no start/end date.'
                    : 'Runs once between a start and end date, then completes.'
                }}
              </p>
            </div>

            <div v-if="!isRecurringReminder" class="tk-dialog__field-row">
              <div class="tk-dialog__field">
                <label class="tk-dialog__label">Start date<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
                <v-text-field
                  v-model="form.start_date"
                  type="date"
                  variant="outlined"
                  density="default"
                  hide-details="auto"
                  class="tk-dialog__input"
                  :rules="[required('Start date')]"
                  :disabled="saving"
                />
              </div>
              <div class="tk-dialog__field">
                <label class="tk-dialog__label">End date<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
                <v-text-field
                  v-model="form.end_date"
                  type="date"
                  variant="outlined"
                  density="default"
                  hide-details="auto"
                  class="tk-dialog__input"
                  :rules="[required('End date'), endAfterStart]"
                  :disabled="saving"
                />
              </div>
            </div>

            <template v-else>
              <div class="tk-dialog__field">
                <label class="tk-dialog__label">Schedule type<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
                <v-select
                  v-model="form.schedule_type"
                  :items="SCHEDULE_TYPE_OPTIONS"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="default"
                  hide-details="auto"
                  class="tk-dialog__input"
                  :rules="[required('Schedule type')]"
                  :disabled="saving"
                />
                <p class="tk-dialog__hint">{{ scheduleTypeHint }}</p>
              </div>

              <field-with-lead-override
                v-if="!isOneTimeSchedule"
                label="Every"
                :field-hint="scheduleFrequencyUnitLabel"
                v-model:value="form.schedule_frequency"
                v-model:override-field="form.schedule_frequency_field"
                value-type="number"
                :value-min="1"
                value-placeholder="e.g. 3"
                :override-options="numberFilterFieldOptions"
                override-placeholder="e.g. Contract Reminder Interval"
                :disabled="saving"
                :can-use-filters="canUseFilters"
              />

              <field-with-lead-override
                v-if="form.schedule_type === 'WEEKLY'"
                label="Day of the week"
                v-model:value="form.schedule_day_of_week"
                v-model:override-field="form.schedule_day_of_week_field"
                value-type="select"
                :value-items="WEEKDAY_OPTIONS"
                :override-options="filterFieldOptions"
                override-placeholder="e.g. Preferred Day"
                :disabled="saving"
                :can-use-filters="canUseFilters"
              />

              <field-with-lead-override
                v-if="form.schedule_type === 'MONTHLY'"
                label="Day of the month"
                v-model:value="form.schedule_day_of_month"
                v-model:override-field="form.schedule_day_of_month_field"
                value-type="select"
                :value-items="DAY_OF_MONTH_OPTIONS"
                :override-options="filterFieldOptions"
                override-placeholder="e.g. Billing Day"
                :disabled="saving"
                :can-use-filters="canUseFilters"
              />

              <div v-if="isOneTimeSchedule" class="tk-dialog__field">
                <label class="tk-dialog__label">Date and time to call</label>
                <p class="tk-dialog__hint">
                  Each lead is called once, at the exact date (and, unless Call time below is set, the
                  exact time too) resolved below.
                </p>
              </div>

              <field-with-lead-override
                v-if="isOneTimeSchedule"
                label="Date to call"
                field-hint="pick one date for every lead, or read each lead's own date field"
                v-model:value="form.anchor_date"
                v-model:override-field="form.anchor_field"
                value-type="date"
                :override-options="dateFilterFieldOptions"
                override-placeholder="e.g. Preferred Call Date"
                :disabled="saving"
                :can-use-filters="canUseFilters"
              />
              <p v-if="isOneTimeSchedule && form.anchor_field" class="tk-dialog__hint tk-dialog__hint--notice" style="margin-top: -10px">
                Two ways to use a lead field: point it at a field that already holds the full date and
                time together (e.g. one populated by another campaign's "Capture agreed time" setting, or
                by a bulk upload) and leave Call time below blank -- or point it at a plain calendar-date
                field and set Call time below for the hour.
              </p>

              <field-with-lead-override
                label="Call time"
                :field-hint="isOneTimeSchedule ? '24-hour, e.g. 14:30 -- optional, see below' : '24-hour, e.g. 14:30'"
                v-model:value="form.schedule_time"
                v-model:override-field="form.schedule_time_field"
                value-type="time"
                :override-options="timeFilterFieldOptions"
                override-placeholder="e.g. Preferred Call Time"
                :disabled="saving"
                :can-use-filters="canUseFilters"
                :required="!isOneTimeSchedule"
              />
              <p v-if="isOneTimeSchedule" class="tk-dialog__hint" style="margin-top: -10px">
                Leave this blank if the date above already resolves to the exact date <em>and</em> time
                together (e.g. a lead field written by another campaign's "Capture agreed time" setting).
                Set it only if that resolves to just a calendar date and the time comes from here instead.
              </p>

              <div v-if="!isOneTimeSchedule" class="tk-dialog__field">
                <label class="tk-dialog__label">
                  {{ form.schedule_type === 'YEARLY' ? 'Reference date' : 'Count from' }}
                </label>
                <v-btn-toggle
                  v-model="anchorMode"
                  mandatory
                  density="comfortable"
                  class="tk-dialog__toggle"
                  :disabled="saving"
                >
                  <v-btn value="CAMPAIGN" class="text-none">
                    {{ form.schedule_type === 'YEARLY' ? 'Same date for every lead' : 'Campaign start date' }}
                  </v-btn>
                  <v-btn value="LEAD_FIELD" class="text-none">A lead field</v-btn>
                </v-btn-toggle>
                <p class="tk-dialog__hint">
                  {{
                    form.schedule_type === 'YEARLY'
                      ? (anchorMode === 'CAMPAIGN'
                          ? 'Every lead is wished on the same calendar date below, repeating on that month/day every year.'
                          : "Each lead's own date in the field below (e.g. Date of Birth) sets the month/day this repeats on every cycle.")
                      : anchorMode === 'CAMPAIGN'
                        ? 'Every lead is reminded on the same shared clock, starting from when this campaign began.'
                        : 'Each lead counts from its own value in the field below (e.g. its own activation date) — staggers naturally.'
                  }}
                </p>
              </div>

              <div v-if="form.schedule_type === 'YEARLY' && anchorMode === 'CAMPAIGN'" class="tk-dialog__field">
                <label class="tk-dialog__label">Date<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
                <v-text-field
                  v-model="form.anchor_date"
                  type="date"
                  variant="outlined"
                  density="default"
                  hide-details="auto"
                  class="tk-dialog__input"
                  :rules="[required('Date')]"
                  :disabled="saving"
                />
                <p class="tk-dialog__hint">Only the month and day are used — the year just anchors the first cycle.</p>
              </div>

              <div v-if="!isOneTimeSchedule && anchorMode === 'LEAD_FIELD'" class="tk-dialog__field">
                <label class="tk-dialog__label">Lead field<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
                <v-select
                  v-model="form.anchor_field"
                  :items="dateFilterFieldOptions"
                  item-title="label"
                  item-value="key"
                  placeholder="e.g. Date of Birth"
                  variant="outlined"
                  density="default"
                  hide-details="auto"
                  class="tk-dialog__input"
                  :rules="[required('Lead field')]"
                  :disabled="saving || !canUseFilters"
                />
                <p v-if="!canUseFilters" class="tk-dialog__hint tk-dialog__hint--notice">
                  Select at least one product above to choose which field the reminder cycle counts from.
                </p>
              </div>
            </template>
          </section>

          <v-divider class="tk-dialog__divider" />

          <section class="tk-dialog__section">
            <p class="tk-dialog__section-title">Calling setup</p>

            <div class="tk-dialog__field">
              <label class="tk-dialog__label">Handled by<span class="tk-dialog__label-required" aria-hidden="true">*</span></label>
              <v-select
                v-model="form.handled_by"
                :items="handledByOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="default"
                hide-details="auto"
                class="tk-dialog__input"
                :rules="[required('Handled by')]"
                :disabled="saving || isRecurringReminder"
                :hint="isRecurringReminder ? 'Recurring reminder campaigns always run through the AI agent.' : undefined"
                :persistent-hint="isRecurringReminder"
              >
                <template #item="{ props: itemProps, item }">
                  <v-list-item
                    v-bind="itemProps"
                    :prepend-icon="item.raw.icon"
                    :subtitle="item.raw.subtitle"
                  />
                </template>
              </v-select>
            </div>

            <div class="tk-dialog__field">
              <label class="tk-dialog__label">
                {{ isAiCampaign ? 'AI agents' : 'Human agents' }}<span class="tk-dialog__label-required" aria-hidden="true">*</span>
              </label>
              <v-select
                v-model="form.assigned_agent_ids"
                :items="agentOptions"
                item-title="title"
                item-value="value"
                :placeholder="agentOptions.length ? 'Select agents' : 'No matching agents found'"
                variant="outlined"
                density="default"
                hide-details="auto"
                class="tk-dialog__input tk-dialog__input--multi"
                multiple
                chips
                closable-chips
                :loading="agentsLoading"
                :rules="[requiredList('Assign at least one agent')]"
                :disabled="saving"
              />
            </div>

            <p v-if="!isEditMode" class="tk-dialog__hint tk-dialog__hint--notice">
              New campaigns start on Open distribution — pick Round robin, Manual,
              Field-based, or Excel-assigned instead from this campaign's Settings
              tab once it exists.
            </p>
            <p v-else class="tk-dialog__hint tk-dialog__hint--notice">
              Currently <strong>{{ currentDistributionLabel }}</strong>. Change how leads are
              split, including a fresh Excel assignment, from this campaign's Settings tab.
            </p>

            <div
              v-if="assignmentPhase === 'uploading' || assignmentPhase === 'processing'"
              class="tk-dialog__processing"
            >
              <v-progress-circular indeterminate size="22" width="2" color="primary" />
              <div>
                <p class="tk-dialog__processing-title">
                  {{
                    assignmentPhase === 'uploading'
                      ? 'Uploading assignments…'
                      : 'Processing leads…'
                  }}
                </p>
                <p class="tk-dialog__hint">
                  {{
                    assignmentPhase === 'uploading'
                      ? 'Sending your spreadsheet to the server.'
                      : isFieldBasedMode
                        ? 'Matching leads to agents from their field values in the background.'
                        : 'Matching rows to leads in the background. This can take a moment for large files.'
                  }}
                </p>
              </div>
            </div>

            <div
              v-if="unmatchedAssignments.length"
              class="tk-dialog__unmatched"
            >
              <p class="tk-dialog__section-title">Unmatched leads</p>
              <p class="tk-dialog__hint">
                Campaign was created
                <template v-if="assignmentMatchSummary">
                  ({{ assignmentMatchSummary }})
                </template>
                . These leads were not assigned — review the reasons below.
              </p>
              <div class="tk-dialog__unmatched-table">
                <div class="tk-dialog__unmatched-head">
                  <span>Identifier</span>
                  <span>Agent email</span>
                  <span>Reason</span>
                </div>
                <div
                  v-for="(row, idx) in unmatchedAssignments"
                  :key="`${row.unique_identifier}-${row.agent_email}-${idx}`"
                  class="tk-dialog__unmatched-row"
                >
                  <span>{{ row.unique_identifier || '—' }}</span>
                  <span>{{ row.agent_email || '—' }}</span>
                  <span>{{ row.reason || '—' }}</span>
                </div>
              </div>
            </div>

            <p class="tk-dialog__hint tk-dialog__hint--notice">
              Lead filters, the call script, transfer routing, auto-dial and lead-adoption
              behavior are all configured from this campaign's own Settings tab after it's
              created — this dialog only covers what's needed to get it started.
            </p>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-footer tk-dialog__footer">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-none tk-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="text-none btn-primary-cta tk-dialog__save"
          rounded="lg"
          :loading="saving"
          :disabled="!isValid || assignmentPhase === 'uploading' || assignmentPhase === 'processing'"
          @click="unmatchedAssignments.length ? closeAfterUnmatched() : submit()"
        >
          {{
            unmatchedAssignments.length
              ? 'Done'
              : assignmentPhase === 'uploading' || assignmentPhase === 'processing'
                ? 'Working…'
                : isEditMode
                  ? 'Save changes'
                  : 'Create campaign'
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { createCampaign, editCampaign, fetchCampaignAssignmentUploadStatus } from '@/api/campaignsApi'
import { fetchContexts } from '@/api/contextsApi'
import { fetchOrgUsers } from '@/api/usersApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList, unwrapApiPayload } from '@/utils/graphql'
import { normalizeContextsList } from '@/utils/contexts'
import { normalizeUserRecord } from '@/utils/session'
import { isAiAgent, resolveListAgentType } from '@/utils/orgUsers'
import {
  CAMPAIGN_DISTRIBUTION_MODES,
  CAMPAIGN_HANDLED_BY_OPTIONS,
} from '@/utils/campaigns'
import {
  normalizeAssignmentUploadStatus,
  normalizeUnmatchedAssignments,
} from '@/utils/excelAssignments'
import {
  buildCampaignFilterFieldOptions,
  campaignFilterFieldKind,
  createEmptyCampaignFilter,
  findCampaignFilterFieldDef,
  getFilterDateInputValue,
  getFilterRangeMax,
  getFilterRangeMin,
  getFilterSingleValue,
  hydrateCampaignFiltersFromApi,
  normalizeCampaignFiltersForApi,
  operatorsForFilterField,
  setFilterRangeMax,
  setFilterRangeMin,
  setFilterSingleValue,
} from '@/utils/campaignFilters'
import { epochToDateInputValue } from '@/utils/leadFieldTypes'
import { readGreetingMessageFromOrg } from '@/utils/agentOtherSettings'
import FieldWithLeadOverride from '@/components/Campaigns/FieldWithLeadOverride.vue'

defineOptions({ name: 'CreateCampaignDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** When set, dialog edits this campaign via editTask. */
  campaign: { type: Object, default: null },
})

// 'created' fires once, exactly when a brand-new campaign's dialog is about
// to close having actually finished (not on an error close) -- lets the
// parent navigate straight to that campaign's Settings tab, where
// everything this dialog no longer asks up front now lives.
const emit = defineEmits(['update:modelValue', 'saved', 'error', 'created'])

const store = useStore()
const isEditMode = computed(() =>
  Boolean(String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim()),
)

const formRef = ref(null)
const isValid = ref(false)
const saving = ref(false)
// True only while the dialog-open watcher is replacing `form.value` wholesale
// (loading a saved campaign, or resetting to defaults). Guards the
// handled_by watcher below, which exists to clear a stale agent selection
// when the USER manually flips the AI/human toggle -- not when we're simply
// hydrating a saved campaign whose handled_by happens to differ from
// whatever the form held a moment before (e.g. the HUMAN_AGENT default, or
// the previous campaign's mode). Without this guard that watcher fires on
// hydration too and wipes the agent selection we just loaded.
const hydrating = ref(false)
const contexts = ref([])
const contextsLoading = ref(false)
const agents = ref([])
const agentsLoading = ref(false)
const unmatchedAssignments = ref([])
/** idle | uploading | processing | done */
const assignmentPhase = ref('idle')
const assignmentMatchSummary = ref('')
const campaignFilters = ref([])
// Set once submit() gets a task_id back from a CREATE (never an edit) --
// read by every "new campaign, actually finishing" close point below to
// emit 'created' with it.
const newlyCreatedTaskId = ref('')
let assignmentPollTimer = null

const ASSIGNMENT_POLL_MS = 3000
const ASSIGNMENT_POLL_MAX_MS = 15 * 60 * 1000

const handledByOptions = CAMPAIGN_HANDLED_BY_OPTIONS

function defaultForm() {
  return {
    task_name: '',
    description: '',
    task_type: 'STANDARD',
    start_date: '',
    end_date: '',
    anchor_field: '',
    anchor_date: '',
    schedule_type: 'DAILY',
    schedule_type_field: '',
    schedule_frequency: null,
    schedule_frequency_field: '',
    schedule_time: '',
    schedule_time_field: '',
    schedule_day_of_week: '',
    schedule_day_of_week_field: '',
    schedule_day_of_month: '',
    schedule_day_of_month_field: '',
    greeting: readGreetingMessageFromOrg(store.state.organization),
    mission_prompt: '',
    disclaimer_enabled: null,
    capture_datetime_field: '',
    capture_datetime_prompt: '',
    context_names: [],
    handled_by: 'HUMAN_AGENT',
    assigned_agent_ids: [],
    distribution_mode: 'OPEN',
    agent_field: '',
    auto_dial_enabled: false,
    // A RECURRING_REMINDER campaign forces auto_dial_enabled to true
    // regardless of this default (see the isAiCampaign watcher below,
    // scoped to task_type === 'RECURRING_REMINDER' only) -- and the backend
    // rejects auto_dial_enabled=true with no working hours or retry
    // cooldown ("...are mandatory when auto_dial_enabled is true"). Since
    // this dialog no longer has input fields for either (moved to the
    // Settings tab), a brand-new recurring campaign needs a valid starting
    // point here or creation fails outright. A plain business day and the
    // dialog's own old placeholder value -- both fully editable afterward.
    // A plain STANDARD AI campaign does NOT force this -- it stays a real,
    // explicit opt-in made on the Settings tab after creation, same as a
    // HUMAN_AGENT campaign.
    working_hours_start: '09:00',
    working_hours_end: '18:00',
    retry_cooldown_minutes: '60',
    max_dial_attempts: '',
    dial_ratio: 1,
    auto_adopt_matching_leads: true,
    only_future_leads: false,
  }
}
const form = ref(defaultForm())

const isRecurringReminder = computed(() => form.value.task_type === 'RECURRING_REMINDER')
// An inbound campaign answers calls rather than placing them, so everything
// about working a lead list -- schedule, filters, distribution, auto-dial --
// is meaningless on it and stays hidden.
// A reminder program only works unattended -- reArmDueReminders just flips a
// lead back to PENDING on schedule, and without the AI dialer picking it up
// automatically it would just sit in a human agent's queue forever. Backend
// (createTask/editTask) rejects RECURRING_REMINDER + HUMAN_AGENT outright, so
// this keeps the form from ever building a payload the backend would reject.
watch(isRecurringReminder, (recurring) => {
  if (recurring) form.value.handled_by = 'AI_AGENT'
})
// 'CAMPAIGN' = every lead anchors to the same shared date -- for
// MONTHLY/WEEKLY/DAILY that's this campaign's own start date (no field or
// input needed, form.anchor_date stays empty); for YEARLY it's an explicit
// date the admin picks (form.anchor_date, shown only in that case -- every
// lead is wished on the same month/day every year, e.g. a company-wide
// Independence Day call). 'LEAD_FIELD' = each lead counts from its own
// value in form.anchor_field instead. Defaults to CAMPAIGN — the simpler
// option requiring no field selection — unless an existing campaign (edit
// mode) already has an anchor_field set, in which case hydrateFormFromCampaign
// switches this to LEAD_FIELD.
const anchorMode = ref('CAMPAIGN')
watch(anchorMode, (mode) => {
  if (mode === 'CAMPAIGN') form.value.anchor_field = ''
  // Guarded off ONE_TIME: that type forces this ref to 'LEAD_FIELD' on
  // every schedule_type change (see the watcher below) purely as inherited
  // state, but ONE_TIME's own "Date to call" input owns form.anchor_date
  // directly and must not have it wiped out by that side effect.
  else if (mode === 'LEAD_FIELD' && form.value.schedule_type !== 'ONE_TIME') form.value.anchor_date = ''
})

const SCHEDULE_TYPE_OPTIONS = [
  { value: 'YEARLY', title: 'Yearly' },
  { value: 'MONTHLY', title: 'Monthly' },
  { value: 'WEEKLY', title: 'Weekly' },
  { value: 'DAILY', title: 'Daily' },
  { value: 'ONE_TIME', title: 'Once, at a time on the lead' },
]
const WEEKDAY_OPTIONS = [
  { value: 'MON', title: 'Monday' },
  { value: 'TUE', title: 'Tuesday' },
  { value: 'WED', title: 'Wednesday' },
  { value: 'THU', title: 'Thursday' },
  { value: 'FRI', title: 'Friday' },
  { value: 'SAT', title: 'Saturday' },
  { value: 'SUN', title: 'Sunday' },
]
const DAY_OF_MONTH_OPTIONS = [
  ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), title: String(i + 1) })),
  { value: 'LAST', title: 'Last day of the month' },
]

const scheduleTypeHint = computed(() => {
  switch (form.value.schedule_type) {
    case 'YEARLY':
      return 'Repeats every N years on a chosen month/day — either every lead’s own reference date (e.g. a birthday) or one fixed date shared by everyone (e.g. an annual company-wide greeting).'
    case 'MONTHLY':
      return 'Repeats every N months on a chosen day of the month.'
    case 'WEEKLY':
      return 'Repeats every N weeks on a chosen day of the week.'
    case 'DAILY':
      return 'Repeats every N days -- same as this campaign’s old flat interval, just named explicitly.'
    case 'ONE_TIME':
      return 'Calls each lead once, at the exact date and time held in the field below, then never again. Use for a slot the lead agreed to — an interview, a scheduled callback. Leads with no time in that field are simply never called.'
    default:
      return ''
  }
})
const scheduleFrequencyUnitLabel = computed(() => {
  switch (form.value.schedule_type) {
    case 'YEARLY': return 'year(s)'
    case 'MONTHLY': return 'month(s)'
    case 'WEEKLY': return 'week(s)'
    default: return 'day(s)'
  }
})
// ONE_TIME owns form.anchor_date/anchor_field directly through its own
// "Date to call" input (see the template, gated on isOneTimeSchedule) --
// anchorMode plays no role for it either way. Still forced to LEAD_FIELD
// here, unchanged from before, purely so a stray CAMPAIGN-mode watch
// (above) never clears form.anchor_date out from under that input when
// schedule_type lands on ONE_TIME. YEARLY is NOT forced -- since it now
// supports both a shared date and a per-lead field, anchorMode is left
// exactly as the admin (or hydrateFormFromCampaign, on edit) set it.
watch(
  () => form.value.schedule_type,
  (type) => {
    if (type === 'ONE_TIME') anchorMode.value = 'LEAD_FIELD'
  },
)
// ONE_TIME reads the date AND the time of day straight off the lead's field,
// so every recurrence input (how often, which weekday, which day of the month,
// what time) is meaningless for it and is hidden rather than shown-and-ignored.
const isOneTimeSchedule = computed(() => form.value.schedule_type === 'ONE_TIME')
const isAiCampaign = computed(() => form.value.handled_by === 'AI_AGENT')
const isHumanCampaign = computed(() => form.value.handled_by === 'HUMAN_AGENT')
const isExcelMode = computed(() => form.value.distribution_mode === 'EXCEL')
const isFieldBasedMode = computed(() => form.value.distribution_mode === 'FIELD_BASED')
const isAsyncAssignmentMode = computed(
  () => isExcelMode.value || isFieldBasedMode.value,
)
// AI campaigns are narrowed to the modes that actually assign a specific
// agent per lead -- matches the backend's own createTask/editTask
// validation. OPEN never assigns anyone (every lead would silently fall
// back to the campaign's first agent); FIELD_BASED matches by the agent's
// email, which AI agents don't have; CREATOR_BASED matches the lead's
// creator, and leads are never created "as" an AI agent. Distribution mode
// itself is no longer picked here (see the Settings tab / CampaignAdvancedSettings.vue)
// -- this Set is still needed below, for the handled_by watcher's own
// correction when it flips a campaign into AI_AGENT while sitting on a
// mode only valid for human campaigns.
const AI_CAMPAIGN_DISTRIBUTION_MODES = new Set(['ROUND_ROBIN', 'EXCEL', 'MANUAL'])
// Read-only label shown in edit mode in place of the old dropdown.
const currentDistributionLabel = computed(() => {
  const mode = String(form.value.distribution_mode ?? '').toUpperCase()
  return CAMPAIGN_DISTRIBUTION_MODES.find((m) => m.value === mode)?.title || mode || '—'
})

// The Product field is single-select in the UI, but form.context_names stays
// an array everywhere else -- editTask's payload, filter-field resolution,
// the KB scoping pipeline -- all already expect a list, and no live campaign
// has ever used more than one product. Narrowing the wire format down to a
// single string would touch the backend and every one of those call sites
// for no benefit; this computed is the one place that reconciles "pick one"
// UX with "array" storage.
const selectedContext = computed({
  get: () => (Array.isArray(form.value.context_names) ? form.value.context_names[0] : undefined) ?? null,
  set: (value) => {
    form.value.context_names = value ? [value] : []
  },
})

const selectedContexts = computed(() => {
  const names = new Set(
    (Array.isArray(form.value.context_names) ? form.value.context_names : []).map(
      (name) => String(name ?? '').trim(),
    ),
  )
  return contexts.value.filter((context) => names.has(context.context_name))
})

const filterFieldOptions = computed(() =>
  buildCampaignFilterFieldOptions(selectedContexts.value),
)

// Anchor-field dropdown for a RECURRING_REMINDER campaign — same field
// list as filters/agent-field, narrowed to date-typed fields (the reminder
// cycle counts days from whatever date this field holds, e.g. an
// "Activation Date" or "Install Date" mapped during bulk upload).
const dateFilterFieldOptions = computed(() =>
  filterFieldOptions.value.filter((field) => campaignFilterFieldKind(field) === 'date'),
)

// Interval-field dropdown — same idea, narrowed to number-typed fields
// (the reminder frequency itself, in days, e.g. a per-customer contract
// term).
const numberFilterFieldOptions = computed(() =>
  filterFieldOptions.value.filter((field) => campaignFilterFieldKind(field) === 'number'),
)

// Per-lead call-time override -- narrowed to TIME-typed fields specifically
// (not campaignFilterFieldKind's own date/number/text buckets, which don't
// have a time-of-day concept at all).
const timeFilterFieldOptions = computed(() =>
  filterFieldOptions.value.filter((field) => String(field.type ?? '').toUpperCase() === 'TIME'),
)

const canUseFilters = computed(() => form.value.context_names.length > 0)

const contextOptions = computed(() =>
  contexts.value
    .filter((context) => !context.disabled)
    .map((context) => ({
      title: context.context_name,
      value: context.context_name,
    })),
)

// createCampaign expects agent user_ids matching the handled_by type. The
// backend also lets an OWNER/ADMIN work a HUMAN_AGENT task themselves (they
// count as a human agent by default) -- only an AGENT-role user needs the
// AI/human agent_type split, since an admin is never an AI agent.
const agentOptions = computed(() =>
  agents.value
    .filter((user) => {
      const role = String(user.role ?? '').toUpperCase()
      if (role === 'AGENT') {
        return isAiCampaign.value
          ? isAiAgent(resolveListAgentType(user))
          : !isAiAgent(resolveListAgentType(user))
      }
      return !isAiCampaign.value && ['OWNER', 'ADMIN'].includes(role)
    })
    .map((user) => ({ title: user.name || user.email || user.id, value: user.id })),
)

function required(label) {
  return (value) => Boolean(String(value ?? '').trim()) || `${label} is required`
}

function requiredList(message) {
  return (value) => (Array.isArray(value) && value.length > 0) || message
}


function toDateInputValue(value) {
  const text = String(value ?? '').trim()
  if (!text) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10)
  return epochToDateInputValue(value) || ''
}

function formFromCampaign(campaign) {
  if (!campaign) return defaultForm()
  return {
    task_name: String(campaign.name ?? '').trim(),
    description: String(campaign.description ?? '').trim(),
    task_type: String(campaign.taskType ?? 'STANDARD').toUpperCase() || 'STANDARD',
    start_date: toDateInputValue(campaign.startDate),
    end_date: toDateInputValue(campaign.endDate),
    anchor_field: String(campaign.anchorField ?? '').trim(),
    anchor_date: toDateInputValue(campaign.anchorDate),
    schedule_type: String(campaign.scheduleType ?? '').trim().toUpperCase() || 'DAILY',
    schedule_type_field: String(campaign.scheduleTypeField ?? '').trim(),
    schedule_frequency: campaign.scheduleFrequency != null ? Number(campaign.scheduleFrequency) : null,
    schedule_frequency_field: String(campaign.scheduleFrequencyField ?? '').trim(),
    schedule_time: String(campaign.scheduleTime ?? '').trim(),
    schedule_time_field: String(campaign.scheduleTimeField ?? '').trim(),
    schedule_day_of_week: String(campaign.scheduleDayOfWeek ?? '').trim().toUpperCase(),
    schedule_day_of_week_field: String(campaign.scheduleDayOfWeekField ?? '').trim(),
    schedule_day_of_month: String(campaign.scheduleDayOfMonth ?? '').trim(),
    schedule_day_of_month_field: String(campaign.scheduleDayOfMonthField ?? '').trim(),
    // The campaign's own real current greeting/mission prompt (from its
    // auto-created action) -- shown as-is for review, editable, and only
    // sent back if actually changed.
    greeting: String(campaign.greeting ?? '').trim(),
    mission_prompt: String(campaign.missionPrompt ?? '').trim(),
    disclaimer_enabled: campaign.disclaimerEnabled ?? null,
    capture_datetime_field: String(campaign.captureDatetimeField ?? '').trim(),
    capture_datetime_prompt: String(campaign.captureDatetimePrompt ?? '').trim(),
    context_names: Array.isArray(campaign.contextNames)
      ? [...campaign.contextNames]
      : [],
    handled_by: String(campaign.handledBy ?? 'HUMAN_AGENT').toUpperCase() || 'HUMAN_AGENT',
    assigned_agent_ids: Array.isArray(campaign.assignedAgentIds)
      ? [...campaign.assignedAgentIds]
      : [],
    distribution_mode:
      String(campaign.distributionMode ?? 'OPEN').toUpperCase() || 'OPEN',
    agent_field: String(campaign.agentField ?? '').trim(),
    auto_dial_enabled: Boolean(campaign.autoDialEnabled),
    working_hours_start: String(campaign.workingHoursStart ?? '').trim(),
    working_hours_end: String(campaign.workingHoursEnd ?? '').trim(),
    retry_cooldown_minutes:
      campaign.retryCooldownMinutes != null
        ? String(campaign.retryCooldownMinutes)
        : '',
    max_dial_attempts:
      campaign.maxDialAttempts != null ? String(campaign.maxDialAttempts) : '',
    // Softphone predictive dial_ratio (1–10). UI/API keep live value at 1.
    dial_ratio:
      campaign.dialRatio != null
        ? Math.min(10, Math.max(1, Number(campaign.dialRatio) || 1))
        : 1,
    auto_adopt_matching_leads: campaign.autoAdoptMatchingLeads !== false,
    // Only takes effect on a save that itself changes lead
    // distribution/products/filters -- otherwise this is a no-op either way.
    only_future_leads: !!campaign.onlyFutureLeads,
  }
}
function hydrateFiltersFromCampaign(campaign) {
  const fromApi = hydrateCampaignFiltersFromApi(campaign?.filters)
  if (fromApi.length) {
    campaignFilters.value = fromApi
    return
  }
  campaignFilters.value = []
}

function endAfterStart(value) {
  const start = String(form.value.start_date ?? '').trim()
  const end = String(value ?? '').trim()
  if (!start || !end) return true
  return end >= start || 'End date must be after the start date'
}

function requiredWorkingHoursTime(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return 'Working hours are required when auto-dial is on'
  return (
    /^([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(raw) ||
    'Pick a valid time'
  )
}

function endAfterWorkingStart(value) {
  const start = String(form.value.working_hours_start ?? '').trim().slice(0, 5)
  const end = String(value ?? '').trim().slice(0, 5)
  if (!start || !end) return true
  return end > start || 'End must be after start'
}

function requiredRetryCooldown(value) {
  const cooldown = Number(value)
  return (
    (Number.isFinite(cooldown) && cooldown > 0 && Number.isInteger(cooldown)) ||
    'Enter cooldown minutes (required, no default)'
  )
}

function maxDialAttemptsRule(value) {
  if (value === '' || value == null) return true
  const attempts = Number(value)
  return (
    (Number.isFinite(attempts) && attempts > 0 && Number.isInteger(attempts)) ||
    'Enter a whole number of 1 or more, or leave blank'
  )
}

function resetAssignmentState() {
  stopAssignmentPoll()
  unmatchedAssignments.value = []
  assignmentPhase.value = 'idle'
  assignmentMatchSummary.value = ''
}

function resetFilters() {
  campaignFilters.value = []
}

function addFilterRow() {
  campaignFilters.value = [...campaignFilters.value, createEmptyCampaignFilter()]
}

function removeFilterRow(index) {
  campaignFilters.value = campaignFilters.value.filter((_, i) => i !== index)
}

function onFilterFieldChange(filter) {
  filter.values = []
  if (filter.field && typeof filter.field === 'object') {
    filter.field = String(filter.field.key ?? filter.field.value ?? '').trim()
  }
  const ops = operatorsForRow(filter)
  if (!ops.some((op) => op.value === filter.operator)) {
    filter.operator = ops[0]?.value || 'EQUALS'
  }
}

function onFilterOperatorChange(filter) {
  filter.values = []
}

function ensureDefaultFilterRow() {
  if (!canUseFilters.value || campaignFilters.value.length) return
  campaignFilters.value = [createEmptyCampaignFilter()]
}

function filterRowDef(filter) {
  return findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
}

function filterRowKind(filter) {
  return campaignFilterFieldKind(filterRowDef(filter))
}

function operatorsForRow(filter) {
  return operatorsForFilterField(filterRowDef(filter))
}

function isRangeOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'BETWEEN'
}

function isDateCompareOperator(operator) {
  const op = String(operator ?? '').toUpperCase()
  return op === 'AFTER' || op === 'BEFORE'
}

function isNumericCompareOperator(operator) {
  const op = String(operator ?? '').toUpperCase()
  return op === 'GREATER_THAN' || op === 'LESS_THAN'
}

function filterValueOptions(filter) {
  const def = filterRowDef(filter)
  return def?.type === 'DROPDOWN' && Array.isArray(def.options) ? def.options : []
}

function filterValuesPlaceholder(filter) {
  const def = filterRowDef(filter)
  if (def?.type === 'DROPDOWN') return 'Select value'
  return 'Value'
}

function stopAssignmentPoll() {
  if (assignmentPollTimer) {
    clearTimeout(assignmentPollTimer)
    assignmentPollTimer = null
  }
}

function sleep(ms) {
  return new Promise((resolve) => {
    assignmentPollTimer = setTimeout(resolve, ms)
  })
}

/**
 * Poll getTaskAssignmentUploadStatus until COMPLETED / FAILED or timeout.
 */
async function pollAssignmentStatus(uploadId) {
  const started = Date.now()
  while (Date.now() - started < ASSIGNMENT_POLL_MAX_MS) {
    await sleep(ASSIGNMENT_POLL_MS)
    assignmentPollTimer = null

    const response = await fetchCampaignAssignmentUploadStatus({ upload_id: uploadId })
    if (!isApiSuccess(response)) {
      continue
    }

    const statusPayload = normalizeAssignmentUploadStatus(response)
    const status = statusPayload.status

    if (status === 'COMPLETED' || status === 'FAILED') {
      return statusPayload
    }
  }

  return {
    uploadId,
    status: 'FAILED',
    matchedCount: null,
    unmatchedCount: null,
    unmatched: [],
    timedOut: true,
  }
}

function applyAssignmentStatusResult(statusPayload) {
  if (statusPayload.timedOut) {
    emit(
      'error',
      'Assignment is still processing. Refresh Campaigns in a moment — the campaign will become Active when ready.',
    )
    assignmentPhase.value = 'idle'
    emit('update:modelValue', false)
    return
  }

  if (statusPayload.status === 'FAILED') {
    emit(
      'error',
      isFieldBasedMode.value
        ? 'Lead assignment failed. Check the agent field and assigned agents, then try again.'
        : 'Lead assignment failed. Check the spreadsheet and try again.',
    )
    assignmentPhase.value = 'idle'
    return
  }

  const matched = statusPayload.matchedCount
  const unmatchedCount = statusPayload.unmatchedCount
  const alreadyPresent = statusPayload.alreadyPresentCount
  const parts = []
  if (matched != null) parts.push(`${matched} matched`)
  if (alreadyPresent != null && alreadyPresent > 0) {
    parts.push(`${alreadyPresent} already in campaign`)
  }
  if (unmatchedCount != null) parts.push(`${unmatchedCount} unmatched`)
  assignmentMatchSummary.value = parts.join(', ')

  const unmatched =
    statusPayload.unmatched.length > 0
      ? statusPayload.unmatched
      : normalizeUnmatchedAssignments(statusPayload)

  assignmentPhase.value = 'done'
  emit(
    'saved',
    isEditMode.value
      ? 'Lead selection finished. Existing leads were kept.'
      : 'Lead assignments finished.',
  )

  if (unmatched.length) {
    unmatchedAssignments.value = unmatched
    return
  }

  if (newlyCreatedTaskId.value) emit('created', newlyCreatedTaskId.value)
  emit('update:modelValue', false)
}

async function loadContexts() {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId) return

  contextsLoading.value = true
  try {
    const response = await fetchContexts({ org_id: orgId })
    contexts.value = isApiSuccess(response)
      ? normalizeContextsList(unwrapApiList(response))
      : []
  } catch {
    contexts.value = []
  } finally {
    contextsLoading.value = false
  }
}

async function loadAgents() {
  agentsLoading.value = true
  try {
    const response = await fetchOrgUsers({})
    agents.value = isApiSuccess(response)
      ? unwrapApiList(response).map(normalizeUserRecord).filter(Boolean)
      : []
  } catch {
    agents.value = []
  } finally {
    agentsLoading.value = false
  }
}

function close() {
  if (saving.value) return
  stopAssignmentPoll()
  emit('update:modelValue', false)
}

function closeAfterUnmatched() {
  stopAssignmentPoll()
  emit('update:modelValue', false)
}

function resolveCreateCampaignPayload(response) {
  const root = unwrapApiPayload(response) ?? response?.data ?? response
  if (!root || typeof root !== 'object') return {}
  return root
}

async function submit() {
  if (unmatchedAssignments.value.length) {
    closeAfterUnmatched()
    return
  }

  const validation = await formRef.value?.validate?.()
  if (validation && validation.valid === false) return

  // Belt-and-braces on top of FieldWithLeadOverride's own :rules -- a
  // native <input type="date"> (Date to call's FIXED-mode input) doesn't
  // reliably participate in Vuetify's v-form validation the way a plain
  // text field does, so an empty one can slip past formRef.validate()
  // without failing it (confirmed live 2026-08-13: Samsung Outbound C5 hit
  // the backend's "Either anchor_date or anchor_field is mandatory" error
  // with no client-side warning at all). Checked explicitly here so this
  // is always caught before the request ever leaves the browser.
  if (
    isOneTimeSchedule.value &&
    !String(form.value.anchor_date ?? '').trim() &&
    !String(form.value.anchor_field ?? '').trim()
  ) {
    emit(
      'error',
      'Date to call is required — pick a fixed date ("Same for every lead") or a lead field ("A lead field") above.',
    )
    return
  }
  // Same belt-and-braces reasoning as above, for YEARLY's own native
  // date input (the "Date" field shown only in CAMPAIGN mode).
  if (
    form.value.schedule_type === 'YEARLY' &&
    anchorMode.value === 'CAMPAIGN' &&
    !String(form.value.anchor_date ?? '').trim()
  ) {
    emit(
      'error',
      'Date is required — pick a date, or switch to "A lead field" above.',
    )
    return
  }

  saving.value = true
  try {
    const filters = normalizeCampaignFiltersForApi(
      campaignFilters.value,
      filterFieldOptions.value,
    )
    const payload = {
      task_name: form.value.task_name,
      description: form.value.description,
      context_names: form.value.context_names,
      assigned_agent_ids: form.value.assigned_agent_ids,
      distribution_mode: form.value.distribution_mode,
      handled_by: form.value.handled_by,
      filters,
      auto_adopt_matching_leads: Boolean(form.value.auto_adopt_matching_leads),
    }
    // Now valid on both createTaskInput and editTaskInput -- on an edit it
    // only takes effect if this same save also changes lead
    // distribution/products/filters, otherwise the backend just ignores it.
    payload.only_future_leads = Boolean(form.value.only_future_leads)
    // Only meaningful for AI_AGENT campaigns; blank means "leave whatever's
    // already configured" on edit (createCampaign always sends it when
    // present since the field is pre-filled from the org default there).
    if (isAiCampaign.value && String(form.value.greeting ?? '').trim()) {
      payload.greeting = String(form.value.greeting).trim()
    }
    // On create, a non-empty value here is the admin's own DRAFT -- the
    // backend treats it as the primary source and polishes it via AI
    // rather than storing it verbatim (see generateTaskMissionPrompt's
    // user_draft param). On edit, it's the actual final text, saved as-is.
    if (isAiCampaign.value && String(form.value.mission_prompt ?? '').trim()) {
      payload.mission_prompt = String(form.value.mission_prompt).trim()
    }
    // Unlike greeting/mission_prompt above, always sent (even when null) --
    // null IS a meaningful, distinct value here ("inherit the organisation's
    // own setting"), not just an empty/unset field, and on edit it's the
    // only way to CLEAR a previously-set per-campaign override back to that.
    if (isAiCampaign.value) {
      payload.disclaimer_enabled = form.value.disclaimer_enabled
      // Sent even when blank: on edit, clearing the select is how an org stops
      // a campaign writing times onto its leads, and only an explicit empty
      // value can express that. Inbound campaigns place no calls, so there is
      // nothing for them to capture.
      payload.capture_datetime_field = String(form.value.capture_datetime_field ?? '').trim()
      payload.capture_datetime_prompt = String(form.value.capture_datetime_prompt ?? '').trim()
    }
    if (isRecurringReminder.value) {
      if (!isEditMode.value) payload.task_type = 'RECURRING_REMINDER'
      payload.anchor_field = String(form.value.anchor_field ?? '').trim()
      payload.anchor_date = String(form.value.anchor_date ?? '').trim()
      payload.schedule_type = form.value.schedule_type
      payload.schedule_type_field = form.value.schedule_type_field
      payload.schedule_frequency = form.value.schedule_frequency
      payload.schedule_frequency_field = form.value.schedule_frequency_field
      payload.schedule_time = form.value.schedule_time
      payload.schedule_time_field = form.value.schedule_time_field
      payload.schedule_day_of_week = form.value.schedule_day_of_week
      payload.schedule_day_of_week_field = form.value.schedule_day_of_week_field
      payload.schedule_day_of_month = form.value.schedule_day_of_month
      payload.schedule_day_of_month_field = form.value.schedule_day_of_month_field
    } else {
      if (!isEditMode.value) payload.task_type = 'STANDARD'
      payload.start_date = form.value.start_date
      payload.end_date = form.value.end_date
    }
    if (isFieldBasedMode.value) {
      payload.agent_field = String(form.value.agent_field ?? '').trim()
    } else if (isEditMode.value) {
      payload.agent_field = ''
    }
    // This dialog has NO auto-dial UI at all (see its own subtitle: "...
    // and auto-dial live on this campaign's Settings tab") -- so whatever
    // is in form.value.auto_dial_enabled here is never something the admin
    // actually chose IN THIS DIALOG. For HUMAN_AGENT that's harmless (it's
    // just the hydrated value, round-tripped -- nothing here ever mutates
    // it). For AI_AGENT it used to be force-clobbered to true by a watcher
    // above regardless of whether the campaign was one-time or recurring,
    // which meant saving something as unrelated as a rename through this
    // dialog could silently re-enable auto-dial on a one-time campaign an
    // admin had deliberately turned off -- the exact incident shape from
    // 2026-08-18, bypassing the confirm-before-enabling safeguard
    // CampaignAdvancedSettings.vue built for it. Confirmed live 2026-08-26:
    // a real dev campaign was one "Edit basics" save away from this firing.
    //
    // Fix: only ever send an auto-dial state this dialog can actually
    // vouch for. RECURRING_REMINDER+AI_AGENT is forced server-side anyway
    // (createTask/editTask), so sending true here just keeps local state
    // honest. Everything else -- including plain STANDARD AI_AGENT --
    // omits auto_dial_enabled (and everything below it) entirely, so
    // editTask/createTask fall back to task.auto_dial_enabled (leave
    // unchanged), the same way every other field this dialog doesn't
    // expose already behaves.
    if (isHumanCampaign.value) {
      payload.auto_dial_enabled = Boolean(form.value.auto_dial_enabled)
      if (form.value.auto_dial_enabled) {
        payload.working_hours_start = String(
          form.value.working_hours_start ?? '',
        ).trim()
        payload.working_hours_end = String(
          form.value.working_hours_end ?? '',
        ).trim()
        payload.retry_cooldown_minutes = Number(
          form.value.retry_cooldown_minutes,
        )
        payload.max_dial_attempts = form.value.max_dial_attempts
          ? Number(form.value.max_dial_attempts)
          : null
        // HUMAN softphone predictive: dial_ratio Int (schema). Locked at 1
        // until phone-system parallel connect queuing is ready.
        payload.dial_ratio = 1
      }
    } else if (isAiCampaign.value && isRecurringReminder.value) {
      payload.auto_dial_enabled = true
      payload.working_hours_start = String(
        form.value.working_hours_start ?? '',
      ).trim()
      payload.working_hours_end = String(
        form.value.working_hours_end ?? '',
      ).trim()
      payload.retry_cooldown_minutes = Number(
        form.value.retry_cooldown_minutes,
      )
      payload.max_dial_attempts = form.value.max_dial_attempts
        ? Number(form.value.max_dial_attempts)
        : null
    }

    if (isEditMode.value) {
      payload.task_id = String(
        props.campaign?.campaignId ?? props.campaign?.id ?? '',
      ).trim()
    }

    const response = isEditMode.value
      ? await editCampaign(payload)
      : await createCampaign(payload)
    if (!isApiSuccess(response)) {
      emit(
        'error',
        getApiMessage(
          response,
          isEditMode.value ? 'Failed to update campaign' : 'Failed to create campaign',
        ),
      )
      return
    }

    const created = resolveCreateCampaignPayload(response)
    if (!isEditMode.value) {
      newlyCreatedTaskId.value = String(created.task_id ?? created.taskId ?? '').trim()
    }
    const uploadUrl = String(created.upload_url ?? created.uploadUrl ?? '').trim()
    const uploadId = String(created.upload_id ?? created.uploadId ?? '').trim()
    const responseStatus = String(
      created.task_status ?? created.status ?? created.taskStatus ?? '',
    )
      .trim()
      .toUpperCase()

    const needsAsync =
      Boolean(uploadUrl || uploadId) ||
      responseStatus === 'PROCESSING' ||
      (isAsyncAssignmentMode.value && !isEditMode.value)

    if (!needsAsync) {
      const unmatched = normalizeUnmatchedAssignments(response)
      emit(
        'saved',
        getApiMessage(
          response,
          isEditMode.value
            ? 'Campaign updated successfully.'
            : 'Campaign created successfully.',
        ),
      )
      if (unmatched.length) {
        unmatchedAssignments.value = unmatched
        return
      }
      if (newlyCreatedTaskId.value) emit('created', newlyCreatedTaskId.value)
      emit('update:modelValue', false)
      return
    }

    emit(
      'saved',
      getApiMessage(
        response,
        isEditMode.value
          ? 'Campaign updated. Re-selecting leads in the background…'
          : 'Campaign created. Processing lead assignments…',
      ),
    )

    if (uploadUrl) {
      // This campaign is Excel-assigned and the product/context change just
      // made changed which leads are in scope -- the backend wants a fresh
      // assignment file to match them, but that file only lives on this
      // campaign's own Settings tab now, not here.
      emit(
        'error',
        "Products changed on an Excel-assigned campaign — upload a fresh assignment file from this campaign's Settings tab to finish adding the new leads.",
      )
      assignmentPhase.value = 'idle'
      return
    }

    if (!uploadId) {
      emit(
        'error',
        uploadUrl
          ? 'Assignment uploaded, but upload_id was missing — refresh Campaigns to check status.'
          : 'Campaign was saved, but upload_id was missing — refresh Campaigns to check status.',
      )
      assignmentPhase.value = 'idle'
      emit('update:modelValue', false)
      return
    }

    assignmentPhase.value = 'processing'
    const statusPayload = await pollAssignmentStatus(uploadId)
    applyAssignmentStatusResult(statusPayload)
  } finally {
    saving.value = false
    if (assignmentPhase.value === 'uploading' || assignmentPhase.value === 'processing') {
      assignmentPhase.value = 'idle'
    }
  }
}

// Switching handled_by invalidates the agent selection (different type).
// Only applies to the user manually flipping the toggle -- see `hydrating`.
watch(
  () => form.value.handled_by,
  (next, prev) => {
    if (hydrating.value) return
    if (!prev || next === prev) return
    form.value.assigned_agent_ids = []
    // Switching INTO AI_AGENT while sitting on a mode only valid for human
    // campaigns (OPEN, FIELD_BASED, CREATOR_BASED) would otherwise leave
    // the dropdown showing a value that's no longer even in its own options
    // list -- and the backend would reject it outright on save.
    if (next === 'AI_AGENT' && !AI_CAMPAIGN_DISTRIBUTION_MODES.has(form.value.distribution_mode)) {
      form.value.distribution_mode = 'ROUND_ROBIN'
    }
  },
)

// A RECURRING_REMINDER always auto-dials -- there's no human-equivalent
// "agent went ready for next call" to opt out of, so unlike HUMAN_AGENT
// this isn't a real choice for it (see the matching backend comment in
// createTask/editTask, which forces the same thing server-side regardless
// of what this sends). Runs during hydration too (no `hydrating` guard,
// unlike the watcher above) -- an older recurring campaign saved before
// this rule existed should come up forced-on here as well, not just on
// the next manual toggle.
//
// Deliberately does NOT fire for a plain STANDARD AI_AGENT campaign --
// that one auto-dials only if an admin explicitly turns it on via the
// Settings tab (CampaignAdvancedSettings.vue), which has its own
// confirm-before-enabling safeguard for exactly this reason. This watcher
// used to fire for every AI campaign regardless of task_type, meaning
// saving anything through THIS dialog (e.g. a plain rename) for an
// existing one-time AI campaign silently forced form.value.auto_dial_enabled
// back to true -- and since this dialog has no auto-dial UI of its own to
// reveal that, an admin who'd deliberately turned auto-dial off would
// never see it happen. Confirmed live 2026-08-26: a real dev campaign was
// one "Edit basics" save away from exactly this, the same incident shape
// as 2026-08-18's runaway one-time campaign. See the payload construction
// below for the matching fix on the save side.
watch(
  isAiCampaign,
  (isAi) => {
    if (isAi && isRecurringReminder.value) form.value.auto_dial_enabled = true
  },
  { immediate: true },
)

watch(
  () => form.value.distribution_mode,
  (mode) => {
    if (mode !== 'EXCEL') resetAssignmentState()
    if (mode !== 'FIELD_BASED') form.value.agent_field = ''
    // Excel's whole point is assigning an uploaded batch of EXISTING
    // leads -- combining it with "only future leads" is a no-op the
    // backend silently ignores, so keep them mutually exclusive here too.
    if (mode === 'EXCEL') form.value.only_future_leads = false
  },
)

watch(
  () => form.value.context_names,
  (names) => {
    const allowed = new Set(filterFieldOptions.value.map((field) => field.key))
    campaignFilters.value = campaignFilters.value
      .map((filter) => {
        if (!filter.field || allowed.has(filter.field)) return filter
        return { ...filter, field: '', values: [] }
      })
      .filter(Boolean)
    if (Array.isArray(names) && names.length) {
      // Don't wipe hydrated edit filters — only seed an empty row when none exist.
      ensureDefaultFilterRow()
    } else if (!isEditMode.value) {
      campaignFilters.value = []
    }
  },
)

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return
    resetAssignmentState()
    newlyCreatedTaskId.value = ''
    await Promise.all([loadContexts(), loadAgents()])
    // hydrating suppresses the handled_by watcher below while we replace
    // form.value wholesale -- see its declaration for why that watcher
    // would otherwise wipe the agent selection we're about to load. Traced
    // live 2026-08-09: form.assigned_agent_ids was provably correct right
    // after this assignment, then reset to [] on the very next tick by
    // that watcher, since handled_by also changes (e.g. the default
    // HUMAN_AGENT -> this campaign's AI_AGENT) as part of the same
    // reassignment. The watcher only needs to fire for the user's own
    // toggle click, not for a saved campaign simply loading its own values.
    hydrating.value = true
    if (isEditMode.value) {
      form.value = formFromCampaign(props.campaign)
      anchorMode.value = form.value.anchor_field ? 'LEAD_FIELD' : 'CAMPAIGN'
      hydrateFiltersFromCampaign(props.campaign)
    } else {
      form.value = defaultForm()
      anchorMode.value = 'CAMPAIGN'
      resetFilters()
    }
    // Stay true through the next render/watcher flush so the handled_by
    // watcher (queued, not synchronous) still sees hydrating as true when
    // it actually runs.
    await nextTick()
    hydrating.value = false
  },
)

onBeforeUnmount(stopAssignmentPoll)
</script>

<style scoped>
.tk-dialog :deep(.dialog-header.tk-dialog__header) {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 24px 28px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  background:
    linear-gradient(180deg, rgba(248, 250, 255, 0.95) 0%, rgba(255, 255, 255, 0.6) 100%);
}

.tk-dialog__header-main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
}

.tk-dialog :deep(.dialog-header__icon.tk-dialog__icon-wrap) {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(145deg, rgba(37, 99, 235, 0.16), rgba(99, 102, 241, 0.1));
  color: #1d4ed8;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.tk-dialog__head-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.tk-dialog :deep(.dialog-header__title) {
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.25;
}

.tk-dialog__subtitle {
  font-size: 0.84rem;
  line-height: 1.45;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog :deep(.dialog-body.tk-dialog__body) {
  padding: 24px 28px 14px;
}

.tk-dialog__section {
  margin-bottom: 6px;
}

.tk-dialog__section-title {
  margin: 0 0 16px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #64748b;
}

.tk-dialog__divider {
  margin: 22px 0;
  opacity: 0.55;
}

.tk-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-bottom: 22px;
  min-width: 0;
}

.tk-dialog__field-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.tk-dialog__field-row .tk-dialog__field {
  margin-bottom: 22px;
}

.tk-dialog__label {
  font-size: 0.74rem;
  font-weight: 650;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #475569;
}

.tk-dialog__label-hint {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.72;
}

/* KAN-14: the disabled Create button gave no clue which fields it was
   waiting on -- every required label now carries this marker so it's
   obvious up front, without needing to touch each field first. */
.tk-dialog__label-required {
  color: #b91c1c;
  margin-left: 2px;
  font-weight: 700;
}

.v-theme--dark .tk-dialog__label-required {
  color: #f87171;
}

.tk-dialog__hint {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__hint--ok {
  color: #047857;
}

.tk-dialog__hint--error {
  color: #b91c1c;
}

.tk-dialog__hint--notice {
  color: #1d4ed8;
  font-weight: 500;
}

.tk-dialog__filters-first {
  margin-top: 10px;
  min-height: 44px !important;
}

.tk-dialog__filters-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tk-dialog__filters-add {
  min-height: 36px;
  letter-spacing: 0;
}

.tk-dialog__filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.tk-dialog__filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.4fr) 40px;
  gap: 10px;
  align-items: start;
}

.tk-dialog__filter-row--range {
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.6fr) 40px;
}

.tk-dialog__filter-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  min-width: 0;
}

.tk-dialog__filter-field,
.tk-dialog__filter-operator,
.tk-dialog__filter-values {
  min-width: 0;
}

.tk-dialog__processing {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 14px;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: rgba(30, 77, 255, 0.06);
  border: 1px solid rgba(30, 77, 255, 0.14);
}

.tk-dialog__processing-title {
  margin: 0 0 0.2rem;
  font-size: 0.86rem;
  font-weight: 700;
  color: #1e40af;
}

.v-theme--dark .tk-dialog__processing {
  background: rgba(129, 140, 248, 0.12);
  border-color: rgba(129, 140, 248, 0.22);
}

.v-theme--dark .tk-dialog__processing-title {
  color: #a5b4fc;
}

.tk-dialog__unmatched {
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.08);
}

.tk-dialog__unmatched-table {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow: auto;
}

.tk-dialog__unmatched-head,
.tk-dialog__unmatched-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.4fr);
  gap: 8px;
  font-size: 0.72rem;
}

.tk-dialog__unmatched-head {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
}

.tk-dialog__unmatched-row {
  color: #78350f;
  padding: 4px 0;
  border-top: 1px solid rgba(245, 158, 11, 0.2);
}

.tk-dialog__input :deep(.v-field) {
  --v-field-border-opacity: 0.4;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.tk-dialog__input :deep(.v-field:hover) {
  --v-field-border-opacity: 0.58;
  background: #ffffff;
  box-shadow:
    0 2px 8px rgba(15, 23, 42, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.tk-dialog__input :deep(.v-field--focused) {
  --v-field-border-opacity: 1;
  background: #ffffff;
  box-shadow:
    0 0 0 4px rgba(37, 99, 235, 0.12),
    0 2px 10px rgba(37, 99, 235, 0.08);
}

.tk-dialog__input :deep(.v-field__overlay) {
  opacity: 0;
}

.tk-dialog__input :deep(.v-chip) {
  font-size: 0.8125rem;
  font-weight: 600;
  height: 28px;
  border-radius: 8px;
}

.tk-dialog__autodial {
  padding: 8px 16px 6px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 14px;
  margin-bottom: 18px;
  background: rgba(248, 250, 252, 0.7);
}

.tk-dialog__autodial .tk-dialog__field {
  margin-bottom: 14px;
}

.tk-dialog :deep(.dialog-footer.tk-dialog__footer) {
  padding: 16px 28px 22px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.4) 0%, #ffffff 100%);
}

.tk-dialog__cancel.v-btn {
  min-width: 108px;
  height: 46px;
  font-size: 0.9rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.38);
  color: #334155;
}

.tk-dialog__save.v-btn {
  min-width: 148px;
  height: 46px;
  font-size: 0.9rem;
  font-weight: 650;
  letter-spacing: -0.01em;
}

.v-theme--dark .tk-dialog__subtitle,
.v-theme--dark .tk-dialog__section-title,
.v-theme--dark .tk-dialog__label {
  color: #94a3b8;
}

.v-theme--dark .tk-dialog :deep(.dialog-header.tk-dialog__header) {
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.2) 100%);
}

.v-theme--dark .tk-dialog :deep(.dialog-header__title) {
  color: #f8fafc;
}

.v-theme--dark .tk-dialog :deep(.dialog-header__icon.tk-dialog__icon-wrap) {
  background: rgba(59, 130, 246, 0.16);
  color: #93c5fd;
}

.v-theme--dark .tk-dialog__input :deep(.v-field) {
  background: rgba(15, 23, 42, 0.55);
  --v-field-border-opacity: 0.42;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.v-theme--dark .tk-dialog__input :deep(.v-field--focused) {
  background: rgba(15, 23, 42, 0.82);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.18);
}

.v-theme--dark .tk-dialog__input :deep(input),
.v-theme--dark .tk-dialog__input :deep(textarea) {
  color: #f8fafc;
}

.v-theme--dark .tk-dialog__cancel.v-btn {
  background: rgba(15, 23, 42, 0.65);
  border-color: rgba(148, 163, 184, 0.3);
  color: #cbd5e1;
}

.v-theme--dark .tk-dialog__autodial {
  background: rgba(15, 23, 42, 0.4);
  border-color: rgba(148, 163, 184, 0.28);
}

.v-theme--dark .tk-dialog :deep(.dialog-footer.tk-dialog__footer) {
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.35) 0%, rgba(15, 23, 42, 0.55) 100%);
}

@media (max-width: 560px) {
  .tk-dialog__field-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .tk-dialog__filter-row {
    grid-template-columns: 1fr;
  }

  .tk-dialog :deep(.dialog-header.tk-dialog__header),
  .tk-dialog :deep(.dialog-body.tk-dialog__body),
  .tk-dialog :deep(.dialog-footer.tk-dialog__footer) {
    padding-left: 18px;
    padding-right: 18px;
  }
}
</style>

<!--
  Unscoped on purpose: App.vue forces .user-dialog-card fields to ~30px with !important.
  These selectors must outrank that compact global rule for the campaign dialog only.
-->
<style>
.user-dialog-card.tk-dialog {
  --y-field-height: 3.5rem;
  --y-field-font-size: 0.95rem;
  --v-input-control-height: 3.5rem;
}

.user-dialog-card.tk-dialog .v-text-field,
.user-dialog-card.tk-dialog .v-select,
.user-dialog-card.tk-dialog .v-autocomplete,
.user-dialog-card.tk-dialog .v-combobox,
.user-dialog-card.tk-dialog .v-textarea,
.user-dialog-card.tk-dialog .v-file-input {
  --v-input-control-height: 3.5rem;
  --y-field-height: 3.5rem;
  --y-field-font-size: 0.95rem;
}

.user-dialog-card.tk-dialog .v-field {
  --v-field-input-padding-top: 0.75rem;
  --v-field-input-padding-bottom: 0.75rem;
  font-size: 0.95rem !important;
}

.user-dialog-card.tk-dialog .v-text-field .v-field,
.user-dialog-card.tk-dialog .v-select .v-field,
.user-dialog-card.tk-dialog .v-autocomplete .v-field,
.user-dialog-card.tk-dialog .v-combobox .v-field,
.user-dialog-card.tk-dialog .v-file-input .v-field {
  min-height: 3.5rem !important;
  font-size: 0.95rem !important;
  border-radius: 14px !important;
}

.user-dialog-card.tk-dialog .v-text-field .v-field__field,
.user-dialog-card.tk-dialog .v-select .v-field__field,
.user-dialog-card.tk-dialog .v-autocomplete .v-field__field,
.user-dialog-card.tk-dialog .v-combobox .v-field__field,
.user-dialog-card.tk-dialog .v-file-input .v-field__field {
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.user-dialog-card.tk-dialog .v-text-field .v-field__input,
.user-dialog-card.tk-dialog .v-select .v-field__input,
.user-dialog-card.tk-dialog .v-autocomplete .v-field__input,
.user-dialog-card.tk-dialog .v-combobox .v-field__input,
.user-dialog-card.tk-dialog .v-file-input .v-field__input,
.user-dialog-card.tk-dialog .v-field--variant-outlined .v-field__input,
.user-dialog-card.tk-dialog .v-field--variant-outlined.v-field--active .v-field__input,
.user-dialog-card.tk-dialog .v-field--variant-outlined.v-field--dirty .v-field__input,
.user-dialog-card.tk-dialog .v-field--variant-outlined.v-field--focused .v-field__input {
  display: flex;
  align-items: center;
  min-height: 3.5rem !important;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  padding-left: 1rem !important;
  padding-right: 0.85rem !important;
  font-size: 0.95rem !important;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.user-dialog-card.tk-dialog .v-text-field input,
.user-dialog-card.tk-dialog .v-select input,
.user-dialog-card.tk-dialog .v-autocomplete input,
.user-dialog-card.tk-dialog .v-combobox input,
.user-dialog-card.tk-dialog .v-textarea textarea {
  font-size: 0.95rem !important;
  font-weight: 500 !important;
  color: var(--y-field-text, #0f172a) !important;
  padding-left: 0.15rem !important;
  margin-left: 0 !important;
}

.user-dialog-card.tk-dialog .v-textarea .v-field {
  min-height: 6.75rem !important;
  border-radius: 14px !important;
}

.user-dialog-card.tk-dialog .v-textarea .v-field__field {
  align-items: flex-start;
}

.user-dialog-card.tk-dialog .v-textarea .v-field__input {
  align-items: flex-start !important;
  min-height: 6rem !important;
  padding-top: 0.95rem !important;
  padding-bottom: 0.95rem !important;
  padding-left: 1rem !important;
  padding-right: 0.85rem !important;
}

.user-dialog-card.tk-dialog .v-field__append-inner {
  padding-top: 0 !important;
  padding-inline-end: 0.35rem !important;
  align-self: center;
}

.user-dialog-card.tk-dialog .v-field__prepend-inner {
  padding-top: 0 !important;
  padding-inline-start: 0.35rem !important;
  align-self: center;
}

.user-dialog-card.tk-dialog .v-chip.v-chip--size-small,
.user-dialog-card.tk-dialog .v-chip {
  height: 1.75rem !important;
  font-size: 0.8125rem !important;
  font-weight: 600;
}
</style>
